import { Component, Element, Event, EventEmitter, h, Listen, Prop, State, Watch } from "@stencil/core";
import Swiper, { Autoplay, Navigation, Pagination, A11y } from "swiper";
import {
  buildEvent,
  getCoverImage,
  getDateFromTimestamp,
  getSizesForResponsiveImage,
  AppStoreType,
  getStyle,
  AppStateType
} from "../../utils/utils";
import { setSliderIndex, setSliderSlidesPerView } from "../../store/slider";
import { isWidgetDisabled } from "../../store/getters";
import { setPopupItems } from "../../store/popup";
import { Unsubscribe } from "../../utils/types";

@Component({
  tag: "covet-pics-gallery-slider",
  styleUrl: "covet-pics-gallery-slider.scss",
})

export class CovetPicsGallerySlider {
  @Element() el: HTMLCovetPicsGallerySliderElement;

  private swiper;

  /**
   * CovetPicsPopup element
   * we don't render() popup in a standard way
   * we inject it at the end </body>
   * because popup needs to be on top all elements
   */
  private popup: HTMLCovetPicsPopupElement;

  /**
   * CovetPicsUpload element
   */
  private upload: HTMLCovetPicsUploadElement;

  /**
   * array of items to be showed in slider
   */
  @Prop() items: Array<any>;

  /**
  * shared item id to open popup or upload widget
  */
  @Prop() sharedItemId: string; 

  /**
   * App store
   */
  @Prop() appStore: AppStoreType;

  // App state;
  private appState: AppStateType;
  private unsubscribe: Unsubscribe;

  @State() isDesktopScreen: boolean;

  /**
   * emitted when "load next page" is initiated
   */
  @Event() nextPage: EventEmitter;

  /**
   * emitted when slider init
   */
  @Event({
    eventName: "sliderInit:covetPics",
  }) sliderInit: EventEmitter;

  /**
   * emitted when slider update
   */
  @Event({
    eventName: "sliderUpdate:covetPics",
  }) sliderUpdate: EventEmitter;

  /**
   * emitted when slider index change
   */
  @Event({
    eventName: "sliderIndexChange:covetPics",
  }) sliderIndexChange: EventEmitter;

  /**
   * after all CovetGalleryItem images loaded, refresh layout
   */
  @Listen("imageLoaded")
  imagesLoadedHandler() {
    const loading = this.el.querySelectorAll("covet-pics-gallery-item[is-loading]").length;

    if (loading === 0 && typeof this.swiper !== "undefined") { this.swiper.update(); }
  }

  @Listen("itemClicked")
  itemClickedHandler(event: CustomEvent) {
    const source = event.detail.itemSource;
    const itemId = event.detail.itemId;

    if (source === "upload_widget") {
      this.upload.openUpload();
    } else {
      this.openPopup(itemId);
    }
  }

  @Watch("items")
  itemsChangeHandler(newValue: Array<any>) {
    this.appStore.dispatch(setPopupItems(newValue));
  }

  @Listen("previousPopupEvent", { target: "window" })
  prevPopupHandler() {
    if (this.swiper.allowSlidePrev) {
      this.swiper.slidePrev();
    }
  }

  @Listen("nextPopupEvent", { target: "window" })
  nextPopupHandler() {
    if (this.swiper.allowSlideNext) {
      this.swiper.slideNext();
    }
  }

  @Listen("elementResize", { target: "window" })
  elementResizeHandler() {
    if (typeof this.swiper !== "undefined") { this.swiper.update(); }
  }

  private getAutoplay() {
    if (this.appState.settings.slideshow_autoplay > 0) {
      return { delay: this.appState.settings.slideshow_autoplay * 1000 };
    } else {
      return false;
    }
  }

  componentWillLoad() {
    this.initStore();
    this.initPopup();
    this.initUploadWidget();
  }

  componentDidLoad() {
    this.initSwiper();
    this.bindCustomUploadWidgetButtons();
    this.emitGalleryReadyEvent();
    this.checkAndOpenPopup();
  }

  componentDidUpdate() {
    this.swiper.slideTo(this.swiper.previousIndex, 0, false);
    this.swiper.update();
    this.unFixSliderheight();
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  private initStore() {
    this.appState = this.appStore.getState();
    this.isDesktopScreen = this.appState.size.isDesktopScreen;
    this.unsubscribe = this.appStore.subscribe(() => {
      this.appState = this.appStore.getState();
      this.isDesktopScreen = this.appState.size.isDesktopScreen;
      // use native tabindex instead of @state to prevent rerender component
      this.setEnableControls(isWidgetDisabled(this.appState));
    });
  }

  private bindCustomUploadWidgetButtons() {
    const selector = this.appState.settings.customer_photo_upload_button_selector;

    if (selector !== "") {
      const nodes = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;
      nodes.forEach(node => {
        node.addEventListener("click", () => this.upload.openUpload());
      });
    }
  }

  private initPopup() {
    if (this.appState.preview.mode) { document.body.querySelectorAll("covet-pics-popup").forEach(node => node.remove()); }

    this.appStore.dispatch(setPopupItems(this.items));
    this.popup = document.createElement("covet-pics-popup");
    this.popup.appStore = this.appStore;
    document.body.appendChild(this.popup);
  }

  private initUploadWidget() {
    if (this.appState.preview.mode) { document.querySelectorAll("covet-pics-upload").forEach(uploadWidget => uploadWidget.remove()); }

    if (this.appState.settings.customer_photo_item_position !== null || this.appState.settings.customer_photo_upload_button_selector !== "") {
      this.upload = document.createElement("covet-pics-upload");
      this.upload.appStore = this.appStore;
      document.body.appendChild(this.upload);
    }
  }

  private initSwiper() {
    const galleryEl = this.el.querySelector(".gallery") as HTMLElement;
    const nextEl = this.el.querySelector(".gallery .swiper-button-next") as HTMLElement;
    const prevEl = this.el.querySelector(".gallery .swiper-button-prev") as HTMLElement;

    const mobileItemPaddingPX = (this.appState.settings.item_mobile_padding / 100) * galleryEl.offsetWidth;
    const desktopItemPaddingPX = (this.appState.settings.item_padding / 100) * galleryEl.offsetWidth;

    const paginationEl = this.el.querySelector(".gallery .swiper-pagination") as HTMLElement;

    this.swiper = new Swiper(galleryEl, {
      modules: [Autoplay, Navigation, Pagination, A11y],
      on: {
        init: (e) => {
          this.sliderInit.emit(e);
          this.appStore.dispatch(setSliderIndex(e.realIndex)); // TODO union
          if (typeof e.params.slidesPerView === "number") { // TODO union
            this.appStore.dispatch(setSliderSlidesPerView(e.params.slidesPerView));
          }
        }, 
        update: (e) => { 
          this.sliderUpdate.emit(e);
          this.appStore.dispatch(setSliderIndex(e.realIndex)); // TODO union
          if (typeof e.params.slidesPerView === "number") { // TODO union
            this.appStore.dispatch(setSliderSlidesPerView(e.params.slidesPerView));
          }
        },
        realIndexChange: (e) => { 
          this.sliderIndexChange.emit(e);
          this.appStore.dispatch(setSliderIndex(e.realIndex)); // TODO union
          if (typeof e.params.slidesPerView === "number") { // TODO union
            this.appStore.dispatch(setSliderSlidesPerView(e.params.slidesPerView));
          }
        }
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous item",
        nextSlideMessage: "Next item",
        containerMessage: "Gallery slider",
        containerRoleDescriptionMessage: `Gallery slider ${this.items.length} items`,
        paginationBulletMessage: "Goto item #{{index}}",
        slideRole: "group",
        slideLabelMessage: "" // used custom aria-label
      },
      slidesPerView: this.appState.settings.slideshow_mobile_photos_per_row,
      spaceBetween: mobileItemPaddingPX,
      autoplay: this.getAutoplay(),
      centerInsufficientSlides: true,
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      pagination: {
        el: paginationEl,
        dynamicBullets: true,
        modifierClass: "swiper-pagination-"
      },
      breakpoints: {
        // when window width is >= 800
        800: {
          slidesPerView: this.appState.settings.slideshow_photos_per_row,
          spaceBetween: desktopItemPaddingPX,
          pagination: false
        }
      }
    });

    if (this.appState.settings.slideshow_load_more) {
      // Load new Cells only if autoload enabled
      this.swiper.on("reachEnd", () => {
        this.fixSliderHeight();
        this.nextPage.emit();
      });
    }
  }

  private fixSliderHeight() {
    const swiperWrapper = this.el.querySelector(".swiper-wrapper") as HTMLElement;
    const swiperWrapperHeight = getStyle(swiperWrapper, "height");
    swiperWrapper.style.height = `${swiperWrapperHeight}px`;
  }

  private unFixSliderheight() {
    const swiperWrapper = this.el.querySelector(".swiper-wrapper") as HTMLElement;
    swiperWrapper.style.removeProperty("height");
  }


  private emitGalleryReadyEvent() {
    const galleryReadyEvent = buildEvent("galleryReady:covetPics", { galleryId: this.appState.widgetId.galleryId, itemsCount: this.items.length, el: this.el });
    document.dispatchEvent(galleryReadyEvent);
  }

  private openPopup(itemId) {
    const index = this.items.findIndex(item => item.id && item.id.toString() === itemId);
    this.popup.openPopup(itemId);

    setTimeout(() => {
      this.swiper.slideTo(index);
    }, 300);
  }

  private setEnableControls(disabled: boolean) {
    const nextEl = this.el.querySelector(".gallery .swiper-button-next") as HTMLElement;
    const prevEl = this.el.querySelector(".gallery .swiper-button-prev") as HTMLElement;
    if (nextEl === null || prevEl === null) return false; 
    prevEl.tabIndex = disabled ? -1 : 0;
    nextEl.tabIndex = disabled ? -1 : 0;
    return true;
  }

  private checkAndOpenPopup() {
    if (this.sharedItemId === "none") return false;
    if (this.sharedItemId === "upload_widget") return this.upload.openUpload();

    return this.openPopup(this.sharedItemId);
  }

  private itemImgAltTag(altTag: string, caption: string, idx: number, itemsLength: number, createdTime: string) {
    if (altTag && altTag !== null) return altTag;
    
    return caption && caption.length > 0
      ? `${caption}, slide ${idx + 1}/${itemsLength}, post created ${getDateFromTimestamp(createdTime)}`
      : `slide ${idx + 1}/${itemsLength}, post created ${getDateFromTimestamp(createdTime)}`;
  }

  private renderStandardUploadItem(item, idx: number, itemsLength: number) {
    return (
      <covet-pics-gallery-item
        class="swiper-slide slide"
        item-source={item.source}
        image-url={item.imageUrl}
        item-index={idx}
        aria-label={`click on for upload image in slider, slide ${idx + 1}/${itemsLength},`}
        appStore={this.appStore}>
      </covet-pics-gallery-item>
    );
  }

  private renderDetailUploadItem(item, idx: number, itemsLength: number) { // TODO check why we upload standard gallery item
    return (
      <covet-pics-gallery-item
        class="swiper-slide slide"
        item-source={item.source}
        image-url={item.imageUrl}
        item-index={idx}
        aria-label={`click on for upload image in slider, slide ${idx + 1}/${itemsLength},`}
        appStore={this.appStore}>
      </covet-pics-gallery-item>
    );
  }

  private renderStandardItem(item, idx: number, itemsLength: number) {
    const itemWidth = this.isDesktopScreen
      ? (100 - (this.appState.settings.item_padding * (this.appState.settings.slideshow_photos_per_row - 1))) / this.appState.settings.slideshow_photos_per_row
      : (100 - (this.appState.settings.item_mobile_padding * (this.appState.settings.slideshow_mobile_photos_per_row - 1))) / this.appState.settings.slideshow_mobile_photos_per_row;
    return (
      <covet-pics-gallery-item
        class="swiper-slide"
        image-url={getCoverImage(item.cover_images.medium_resolution, item.images.medium_resolution)}
        image-high-resolution-url={getCoverImage(item.cover_images.high_resolution, item.images.high_resolution)}
        image-responsive-sizes={getSizesForResponsiveImage(this.appState.settings.slideshow_photos_per_row, this.appState.settings.slideshow_mobile_photos_per_row)}
        item-id={item.id}
        item-source={item.source}
        links={item.links}
        url={item.url}
        alt-tag={this.itemImgAltTag(item.alt_tag, item.caption, idx, itemsLength, item.created_time)}
        rating={item.rating !== null ? item.rating : 0}
        user-name={item.username}
        created-time={item.created_time}
        item-type={item.data_type}
        caption={item.caption}
        item-width={itemWidth}
        item-index={idx}
        appStore={this.appStore}>
      </covet-pics-gallery-item>
    );
  }

  private renderDetailItem(item, idx: number, itemsLength: number) {
    const itemWidth = this.isDesktopScreen
      ? (100 - (this.appState.settings.item_padding * (this.appState.settings.slideshow_photos_per_row - 1))) / this.appState.settings.slideshow_photos_per_row
      : (100 - (this.appState.settings.item_mobile_padding * (this.appState.settings.slideshow_mobile_photos_per_row - 1))) / this.appState.settings.slideshow_mobile_photos_per_row;

    return (
      <covet-pics-gallery-item-detail
        class="swiper-slide"
        image-url={getCoverImage(item.cover_images.medium_resolution, item.images.medium_resolution)}
        image-high-resolution-url={getCoverImage(item.cover_images.high_resolution, item.images.high_resolution)}
        image-responsive-sizes={getSizesForResponsiveImage(this.appState.settings.slideshow_photos_per_row, this.appState.settings.slideshow_mobile_photos_per_row)}
        item-id={item.id}
        item-source={item.source}
        item-type={item.data_type}
        item-width={itemWidth}
        lazy-load={true}
        links={item.links}
        url={item.url}
        user-name={item.username}
        caption={item.caption}
        created-time={item.created_time}
        rating={item.rating !== null ? item.rating : 0}
        verified={item.verified}
        alt-tag={this.itemImgAltTag(item.alt_tag, item.caption, idx, itemsLength, item.created_time)}
        aria-label={`slide ${idx + 1} of ${itemsLength} in main slider`}
        appStore={this.appStore}
        item-index={idx}>
      </covet-pics-gallery-item-detail>);
  }

  render() {
    if (this.items !== undefined) {
      return (
        <section class="gallery swiper">
          <button class={`swiper-button-prev ${this.appState.settings.item_style}`} type="button">
            <svg viewBox="0 0 100 100" aria-hidden="true"><path d="M35.5992906,51.1028539 L41.7768136,57.2847304 L41.7768136,61.570226 L30,49.785113 L41.7768136,38 L41.7768136,42.2854956 L35.9938338,48.0725509 L69.1769366,48.0725509 L69.1769366,51.1028539 L35.5992906,51.1028539 Z" class="arrow" transform="translate(100, 100) rotate(180) "></path></svg>
          </button>
          <div class="swiper-wrapper">
            {this.appState.settings.item_style === "standard" && this.items.map((item, idx) =>
              item.source === "upload_widget"
                ? this.renderStandardUploadItem(item, idx, this.items.length)
                : this.renderStandardItem(item, idx, this.items.length)
            )}
            {this.appState.settings.item_style !== "standard" && this.items.map((item, idx) =>
              item.source === "upload_widget"
                ? this.renderDetailUploadItem(item, idx, this.items.length)
                : this.renderDetailItem(item, idx, this.items.length)
            )}
          </div>
          <div class="swiper-pagination"></div>

          <button class={`swiper-button-next ${this.appState.settings.item_style}`} type="button">
            <svg viewBox="0 0 100 100" aria-hidden="true"><path d="M35.5992906,51.1028539 L41.7768136,57.2847304 L41.7768136,61.570226 L30,49.785113 L41.7768136,38 L41.7768136,42.2854956 L35.9938338,48.0725509 L69.1769366,48.0725509 L69.1769366,51.1028539 L35.5992906,51.1028539 Z" class="arrow" transform="translate(100, 100) rotate(180) "></path></svg>
          </button>
        </section>
      );
    }
  }
}
